import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask, task } from 'ember-concurrency';
import { APPLICATION_SIMPLE_STATUS } from 'sh-www/models/application';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { LOAD_APPLICATIONS_DEFAULTS, LOAD_APPLICATIONS_SORTS } from 'sh-www/services/commands/application-commands';

const CLASS_NAME = 'SH-r-applications-submitted';

const PAGE_SIZE = LOAD_APPLICATIONS_DEFAULTS.APPLICATIONS_PER_PAGE;
const RESULTS_SORT = LOAD_APPLICATIONS_SORTS.DATE_STARTED;

const APPLICATION_INCLUDES = [
  'assignment.location',
  'brand.image',
  'buyer',
  'company',
  'location',
  'pitch',
  'services'
];

const APPLICATION_INCLUDES_STRING = APPLICATION_INCLUDES.join(',');

class ApplicationsSubmittedController extends Controller {
  @service api;
  @service router;
  @service store;

  cache = null;
  data = [];
  meta = null;

  get className() {
    return CLASS_NAME;
  }

  @computed('currentPage')
  get disablePreviousPage() {
    return this.currentPage <= 1;
  }

  @computed('currentPage', 'totalPages')
  get disableNextPage() {
    return this.currentPage >= this.totalPages;
  }

  @computed('currentPage', 'totalResults')
  get pageControlsDisplayString() {
    const { totalResults, currentPage } = this;

    const displayPageMin = (currentPage - 1) * PAGE_SIZE + 1;
    const displayPageMax = Math.min(currentPage * PAGE_SIZE, totalResults);

    return `${displayPageMin} - ${displayPageMax} of ${totalResults}`;
  }

  @computed('meta.pagination.current_page')
  get currentPage() {
    return this.meta?.pagination.current_page ?? 0;
  }

  @computed('meta.pagination.total_pages')
  get totalPages() {
    return this.meta?.pagination.total_pages ?? 0;
  }

  @computed('meta.pagination.total')
  get totalResults() {
    return this.meta?.pagination.total ?? 0;
  }

  @restartableTask
  *loadApplications(page) {
    const cache = this.cache;
    const cachedResponse = cache.getPage(page);

    if (cachedResponse) {
      this.setProperties({ ...cachedResponse });
      return;
    }

    const params = {
      cache_key: cache.cacheKey,
      page,
      sort: RESULTS_SORT,
      count: PAGE_SIZE,
      include: APPLICATION_INCLUDES_STRING,
      filter: {
        direct: 0,
        simpleStatus: APPLICATION_SIMPLE_STATUS.NOT_STARTED,
      },
      format: ASSIGNMENT.FORMATS.DEFAULT,
    };

    const response = yield this.api.request('applications', { params });
    const store = this.store;
    store.pushPayload(response);

    const data = response.data.map(({ id }) => store.peekRecord('application', id));
    const meta = response.meta;
    cache.setPage(page, { data, meta });

    this.setProperties({ data, meta });
  }

  @task
  *updatePage(increment) {
    const newPage = this.currentPage + increment;

    if (this.canAdvancePage(newPage)) {
      window.scrollTo({ top: 0 });
      yield this.loadApplications.perform(newPage);
    }
  }

  canAdvancePage(newPage) {
    return (newPage > 0) && (newPage <= this.totalPages);
  }

  @action
  onFindWorkClicked() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.POSTINGS__FIND_WORK);
  }
}

export default ApplicationsSubmittedController;
